import {
  SpoofReportTableFilterProvider,
  useSelectedPage,
  useSelectedPageSize,
} from '@/hooks/spoof_report_table_filters'
import { useState, useEffect } from 'react'
import { Center, Spinner, Text } from '@chakra-ui/react'
import {
  SortType,
  ReportSortingMechanism,
} from '@/utils/reports/report_sorting_mechanism'

import { Order_By, useSearchSpoofingReportsQuery } from '@/generated/graphql'
import { SpoofReportsTable } from '@/components/web2/spoof_reports_table'
import { maxPageCount } from '@/utils'
import { spoofingReportsOrderByClause } from '@/utils/reports/report_sorting_mechanism'
import { AlertTableColumn } from '@/generated/enums'
import { DOPPEL_DARK } from '@/utils/style'
import { Spoofing_Reports_Bool_Exp } from '@/generated/graphql'

type AlertsSubsetTableProps = {
  orgId: string
  spoofReportsWhere: Spoofing_Reports_Bool_Exp
  numAlerts: number
}

const AlertsSubsetTable: React.FC<AlertsSubsetTableProps> = (props) => {
  return (
    <SpoofReportTableFilterProvider>
      <AlertsSubsetTableInner {...props} />
    </SpoofReportTableFilterProvider>
  )
}

const VISIBLE_COLUMNS = [
  AlertTableColumn.ALERT_CREATED,
  AlertTableColumn.URL,
  AlertTableColumn.SOURCE,
  AlertTableColumn.SEVERITY,
  AlertTableColumn.BRAND,
]

const AlertsSubsetTableInner: React.FC<AlertsSubsetTableProps> = ({
  orgId,
  spoofReportsWhere,
  numAlerts,
}) => {
  const [selectedPage] = useSelectedPage()
  const [selectedPageSize] = useSelectedPageSize()
  const [alerts, setAlerts] = useState([])
  const [totalPageCount, setTotalPageCount] = useState(0)
  const [setToRefreshCache, setSetToRefreshCache] = useState(false)

  // sorting
  const [sortingMechanism, setSortingMechanism] = useState<ReportSortingMechanism>({
    sortingType: SortType.created_at,
    orderBy: Order_By.Desc,
  })

  const handleSortingChange = (newSortingMechanism: ReportSortingMechanism) => {
    if (JSON.stringify(newSortingMechanism) !== JSON.stringify(sortingMechanism)) {
      setSortingMechanism(newSortingMechanism)
      setSetToRefreshCache(true)
    }
  }

  const { data: reportsData, loading: loadingReports } = useSearchSpoofingReportsQuery({
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    variables: {
      spoofingReportsWhere: spoofReportsWhere,
      limit: selectedPageSize,
      offset: (selectedPage - 1) * selectedPageSize,
      refresh: setToRefreshCache,
      skipFullTextSearch: true,
      skipNormalSearch: false,
      orderBy: spoofingReportsOrderByClause(null, sortingMechanism),
    },
    skip: !orgId || !spoofReportsWhere,
  })

  useEffect(() => {
    if (reportsData?.spoofing_reports) {
      setAlerts(reportsData.spoofing_reports)
      setTotalPageCount(maxPageCount(numAlerts, selectedPageSize))
    } else {
      setAlerts([])
      setTotalPageCount(0)
    }
  }, [reportsData, setToRefreshCache, selectedPageSize])

  const isLoading = loadingReports || reportsData === undefined

  return (
    <>
      {isLoading ? (
        <Center display="flex" height="100%" justifyContent="center" p={8}>
          <Spinner size="xl" />
        </Center>
      ) : alerts?.length > 0 ? (
        <SpoofReportsTable
          data={alerts}
          fixedVisibleColumns={VISIBLE_COLUMNS}
          isOrgUnifiedView={false}
          loading={isLoading}
          onSortingChange={handleSortingChange}
          openInNewTab={true}
          setToRefreshCache={setToRefreshCache}
          sortingMechanism={sortingMechanism}
          style={{
            backgroundColor: 'transparent',
            _onhover: {
              backgroundColor: DOPPEL_DARK,
            },
            tablePaddingLeft: '30px',
          }}
          totalPageCount={totalPageCount}
        />
      ) : (
        <Center display="flex" height="100%" justifyContent="center" p={8}>
          <Text>No related alerts found.</Text>
        </Center>
      )}
    </>
  )
}

export default AlertsSubsetTable
